import { useEffect } from 'react';
import PrimaryButton from '../../primary-botton';
import CameraPernissons from '../../../assets/svgs/camera-permissions.svg';
import CameraPermissionIllustration from '../../../assets/svgs/exercise/camera-permission-illustration.png';
import cameraAccesRequierd from '../../../assets/animation/camera-access-requier.json';
import { useDevice } from '../../../zustand/devices-store';
import { useLottie } from 'lottie-react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

// "Save videos of my exercises": shown only when the backend offers recording for this session.
const VideoToggle = ({ checked, onChange }) => {
  const { t } = useTranslation('camera-permission');
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      tw="flex items-center justify-between gap-4 w-full lg:w-[80%] rounded-[10px] border border-stroke px-4 py-3 text-start"
    >
      <span tw="grid gap-1">
        <span tw="font-semibold text-[15px] text-text_primary">
          {t('contentSide.recordVideoTitle')}
        </span>
        <span tw="text-[13px] text-text_secondary">{t('contentSide.recordVideoDescription')}</span>
      </span>
      <span
        css={[
          tw`relative shrink-0 w-[44px] h-[24px] rounded-full transition-colors`,
          checked ? tw`bg-Primary_600` : tw`bg-border_stroke`,
        ]}
      >
        <span
          css={[
            tw`absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white transition-all`,
            checked ? tw`start-[22px]` : tw`start-[2px]`,
          ]}
        />
      </span>
    </button>
  );
};

export const CameraAnimation = () => {
  const { View } = useLottie(
    {
      animationData: cameraAccesRequierd,
      loop: true,
      autoplay: true,
    },
    { width: '100%', height: '100%' }
  );
  return (
    <div tw="absolute inset-0 flex items-center justify-center p-2">
      <div tw="w-[250px] md:w-[300px] max-w-full max-h-full aspect-[300/192]">{View}</div>
    </div>
  );
};

const ContentSide = ({
  onContinue,
  selectedMicrophone,
  permissionsGranted,
  permissionChecked,
  cameraInUse,
  isActive,
  videoToggle = null,
}) => {
  const changeAudioDevice = useDevice(state => state.changeAudioDevice);
  const { t } = useTranslation('camera-permission');

  useEffect(() => {
    if (selectedMicrophone) {
      // playSound();
      // localStorage.setItem("audio-device-id", selectedMicrophone);
      changeAudioDevice(selectedMicrophone);
    }
  }, [selectedMicrophone]);

  return (
    // pb clears the iOS home indicator so the CTA isn't sitting under it.
    // min-h-0 + overflow-y-auto because portrait mobile hands this section a
    // fixed 30% row — on a short phone the copy and button scroll inside it
    // rather than spilling out of the track.
    <div tw="flex flex-col justify-center min-h-0 overflow-y-auto px-5 lg:ps-[10%] gap-3 lg:gap-8 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] lg:py-0 lg:pb-0 lg:overflow-visible">
      {permissionsGranted && !cameraInUse && isActive && (
        <div tw="grid gap-2">
          <h1 tw="font-bold text-[24px] lg:text-[48px]">{t('contentSide.mirrorReadyTitle')}</h1>
          <h1 tw="text-gray-text text-[16px] text-[#656565] lg:text-[18px] w-full lg:w-[90%]">
            {t('contentSide.mirrorReadyDescription')}
          </h1>
          <div tw="hidden max-lg:[@media(orientation:landscape)]:flex lg:flex h-[160px] max-lg:[@media(orientation:landscape)]:h-full lg:h-[250px] w-full items-center justify-center">
            <img
              loading="lazy"
              decoding="async"
              src={CameraPernissons}
              tw="h-[100px] max-lg:[@media(orientation:landscape)]:h-[90px] lg:h-[140px]"
            />
          </div>
        </div>
      )}
      {!isActive && permissionsGranted && (
        <div tw="grid gap-2">
          <h1 tw="font-bold text-[24px] lg:text-[48px]">{t('contentSide.allowCameraTitle')}</h1>
          <h1 tw="text-gray-text text-[16px] text-[#656565] lg:text-[18px] w-full lg:w-[90%]">
            {t('contentSide.allowCameraDescription')}
          </h1>
          <div tw="hidden max-lg:[@media(orientation:landscape)]:flex lg:flex h-[160px] max-lg:[@media(orientation:landscape)]:h-full lg:h-[250px] w-full items-center justify-center">
            <img
              loading="lazy"
              decoding="async"
              src={CameraPernissons}
              tw="h-[100px] max-lg:[@media(orientation:landscape)]:h-[90px] lg:h-[140px]"
            />
          </div>
        </div>
      )}
      {!permissionsGranted && !cameraInUse && (
        <div tw="grid gap-2 max-md:gap-3">
          <h1 tw="font-bold text-[24px] lg:text-[48px] max-md:leading-[1.35] max-md:text-[#2E2E2E]">
            {t('contentSide.seeYourselfTitle')}
          </h1>
          <h1 tw="text-gray-text text-[16px] text-[#656565] lg:text-[18px] w-full lg:w-[90%] max-md:text-[15px] max-md:leading-[1.55]">
            {t('contentSide.seeYourselfDescription')}
          </h1>
          <div tw="max-md:hidden bg-[#F4F4F4] h-[200px] lg:h-[250px] w-full flex items-center justify-center relative rounded-[14px]">
            <CameraAnimation />
          </div>
        </div>
      )}
      {/* Camera in use: this whole side is empty (all three copy blocks are
          about a camera there is no access to), so a lone CTA floating in it
          read as a stray button. The same exit lives in the unavailable card
          instead, next to the retry it belongs with. */}
      {videoToggle && permissionsGranted && !cameraInUse && isActive && (
        <div tw="flex justify-center w-full">
          <VideoToggle checked={videoToggle.checked} onChange={videoToggle.onChange} />
        </div>
      )}
      {!cameraInUse && (
        <div tw="flex justify-center w-full">
          <PrimaryButton
            handleClick={onContinue}
            disable={!isActive && permissionsGranted}
            text={
              !permissionsGranted
                ? t('contentSide.continueWithoutCamera')
                : t('contentSide.continue')
            }
            tw="rounded-[6px] border-[0px] text-[14px] py-[12px] w-full lg:w-[80%]"
            css={[
              !permissionsGranted
                ? tw`bg-white border text-black max-md:border-[#BDC4C9] max-md:text-[#2E2E2E] max-md:text-[15px] max-md:font-medium max-md:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]`
                : tw``,
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default ContentSide;
